import { useMemo, RefObject, FC, useCallback } from 'react'
import * as styles from 'components/features/pfm/PageParamsHeader/PageParamsHeader.styl'
import { TimeframedPage } from 'interfaces/pages'
import { PAGE_DEFINITIONS_MAP } from 'constants/pages'
import { PageHeading } from 'components/atoms/Text'
import { TimeframeSelectContainer } from 'components/features/pfm/PageParamsHeader/TimeframeSelectContainer'
import { isCategorizedPage } from 'helpers/pageHelpers'
import { CategorySelect } from 'components/features/pfm/PageParamsHeader/CategorySelect'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { ToggleComparison } from 'components/features/pageFilters/ToggleComparison/ToggleComparison'
import { PAGE_FILTER_PANE } from 'components/features/pageFilters/types'
import { createPluralFormatter } from 'helpers/formatHelpers'
import cn from 'classnames'
import { useEnabledAccounts } from 'components/features/pageFilters/useEnabledAccounts'

const accountsPlural = createPluralFormatter('Account', 'Accounts')

type PageParamsHeaderProps = {
  type: TimeframedPage
  nodeRef?: RefObject<HTMLDivElement>
}

export const PageParamsHeader: FC<PageParamsHeaderProps> = (props) => {
  const { type, nodeRef } = props

  const categoryDropdown = useMemo(() => {
    if (isCategorizedPage(type)) {
      return <CategorySelect type={ type } />
    }
    return null
  }, [type])

  const timeframeDropdown = useMemo(() => (
    <TimeframeSelectContainer type={ type } className={ styles.timeframeSelect } />
  ), [type])

  const categorySection = useMemo(() => {
    if (!categoryDropdown) {
      return null
    }

    return (
      <span>{ categoryDropdown }<br className={ styles.hideOnLarge } /> </span>
    )
  }, [categoryDropdown])

  const dispatch = useStoreDispatch()

  const handlePickAccounts = useCallback(() => {
    if (type === 'future') {
      return
    }
    dispatch(new ModifyFloatingPaneAction({
      type: PAGE_FILTER_PANE,
      mode: 'by-account',
      page: type,
    }))
  }, [dispatch, type])

  const enabledAccounts = useEnabledAccounts()

  const filterByAccountsChild = useMemo(() => {
    if (enabledAccounts.count === 0 || type === 'creditscore') {
      return null
    }

    return (
      <div className={ styles.col } >
        <button onClick={ handlePickAccounts } type='button' data-qa-marker='accounts-filtering' data-qa-type='button'>
          <h4>
            { accountsPlural(enabledAccounts.count) }
          </h4>
          <span data-qa-marker='enabled-accounts' data-qa-type='title'>
            { enabledAccounts.title }
          </span>
        </button>
      </div>
    )
  }, [enabledAccounts.count, enabledAccounts.title, handlePickAccounts, type])

  const headingChild = useMemo(() => {
    if (type === 'future') {
      return (
        <>
          Your <span className={ styles.timeframe }>{ timeframeDropdown }</span>
        </>
      )
    }

    return (
      <>
        <div>
          Your { PAGE_DEFINITIONS_MAP[type].title }
          { !!categorySection && (
            <>
              { ' ' }
              <br />
              in
              { ' ' }
              { categorySection }
            </>
          )}
        </div>
        <CardBlock
          corners='all'
          forceRoundedCorners
          shadowTheme='medium'
          className={ styles.filter }
          withPadding={ false }
        >
          <div className={ styles.cols } >
            <div className={ styles.col } >
              <button type='button'>
                <h4>Timeframe</h4>
                { timeframeDropdown }
              </button>
            </div>
            { filterByAccountsChild }
          </div>
          { type !== 'transactions' && (
            <ToggleComparison className={ styles.bottom } />
          )}
        </CardBlock>
      </>
    )
  }, [type, categorySection, timeframeDropdown, filterByAccountsChild])

  return (
    <header
      className={ cn(styles.header, type === 'future' && styles.futureTheme) }
      data-qa-marker='static-header'
      ref={ nodeRef }
    >
      <PageHeading className={ styles.heading }>
        { headingChild }
      </PageHeading>
    </header>
  )
}
